import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { ISLANDS, SECTORS } from "@/lib/format";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, ForkKnife, HardHat, Storefront, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";
import { usePublicFeatures } from "@/lib/features";

const SECTOR_ICONS = { restauracao: ForkKnife, construcao: HardHat, comercio: Storefront, servicos: Briefcase };

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", nif: "", sector: "", island: "", address: "", phone: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const company = useCompany();
  const extra = !!usePublicFeatures().setores_extra;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sector) return toast.error("Escolha o setor da empresa");
    if (!form.island) return toast.error("Escolha a ilha");
    setBusy(true);
    try {
      await register(form);
      toast.success("Empresa registada! O seu teste de 30 dias começou.");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Registar empresa" subtitle="30 dias grátis, sem cartão.">
      <form onSubmit={submit} className="space-y-3.5" data-testid="register-form">
        <div className="space-y-1.5">
          <Label>Nome da empresa</Label>
          <Input required value={form.company_name} onChange={set("company_name")} data-testid="reg-company" placeholder="Nome comercial" />
        </div>
        <div className="space-y-1.5">
          <Label>Setor</Label>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(SECTORS).filter(([k]) => extra || k === "restauracao" || k === "construcao").map(([k, v]) => {
              const I = SECTOR_ICONS[k];
              return (
                <button type="button" key={k} onClick={() => setForm({ ...form, sector: k })} data-testid={`reg-sector-${k}`}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${form.sector === k ? "border-primary bg-accent text-accent-foreground" : "border-border hover:bg-secondary"}`}>
                  <I size={18} /> {v}
                </button>
              );
            })}
          </div>
          {!extra && <p className="text-xs text-muted-foreground" data-testid="reg-sectors-note">Para já: restaurantes e construção. Outros setores em breve.</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>NIF</Label>
            <Input required value={form.nif} onChange={set("nif")} data-testid="reg-nif" inputMode="numeric" />
          </div>
          <div className="space-y-1.5">
            <Label>Ilha</Label>
            <Select value={form.island} onValueChange={(v) => setForm({ ...form, island: v })}>
              <SelectTrigger data-testid="reg-island"><SelectValue placeholder="Escolher" /></SelectTrigger>
              <SelectContent>{ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Nome do titular</Label>
          <Input required minLength={2} value={form.name} onChange={set("name")} data-testid="reg-name" placeholder="Nome e apelido" />
        </div>
        <div className="space-y-1.5">
          <Label>Morada</Label>
          <Input required minLength={3} value={form.address} onChange={set("address")} data-testid="reg-address" placeholder="Rua, zona, cidade" />
        </div>
        <div className="space-y-1.5">
          <Label>Telemóvel</Label>
          <Input type="tel" required minLength={6} value={form.phone} onChange={set("phone")} data-testid="reg-phone" placeholder="+238 ..." />
        </div>
        <div className="space-y-1.5">
          <Label>E-mail</Label>
          <Input type="email" required value={form.email} onChange={set("email")} data-testid="reg-email" placeholder="nome@empresa.cv" />
        </div>
        <div className="space-y-1.5">
          <Label>Palavra-passe</Label>
          <PasswordInput required minLength={6} value={form.password} onChange={set("password")} testid="reg-password" placeholder="Mínimo 6 caracteres" />
        </div>
        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="register-submit">
          {busy ? <CircleNotch className="animate-spin" /> : "Começar teste de 30 dias"}
        </Button>
        <p className="text-center text-xs text-muted-foreground" data-testid="register-terms-note">
          Ao criar conta aceita os <Link to="/termos" target="_blank" className="font-medium text-primary underline">Termos do {company.product_name}</Link>, um serviço da {company.short_name || "Tavares Groupe"}.
        </p>
        <p className="text-center text-sm text-muted-foreground">
          Já tem conta?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline" data-testid="login-link">Entrar</Link>
        </p>
      </form>
    </AuthShell>
  );
}
